import React, { useState, useEffect } from 'react';
import {
  X,
  DollarSign,
  Calendar,
  Building2,
  Gift,
  Loader2,
  Upload,
  Image,
  Trash2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToISO } from '../../utils/formatters';
import { createRebate, updateRebate, getAvailableRebatePeriods } from '../../api/wholesale/rebates';
import { getCompanies } from '../../api/wholesale/companies';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const RebateFormModal = ({ isOpen, onClose, companyId = null, rebate = null, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState(null);

  const isEditing = !!rebate;

  const [formData, setFormData] = useState({
    company_id: companyId || '',
    rebate_type: 'monthly',
    rebate_month: '',
    rebate_year: new Date().getFullYear(),
    amount: '',
    description: '',
    received_date: formatDateToISO(new Date()),
    receipt_url: ''
  });

  // Reset form when modal opens or rebate changes
  useEffect(() => {
    if (isOpen) {
      if (rebate) {
        setFormData({
          company_id: rebate.company_id || companyId || '',
          rebate_type: rebate.rebate_type || 'monthly',
          rebate_month: rebate.rebate_month || '',
          rebate_year: rebate.rebate_year || new Date().getFullYear(),
          amount: rebate.amount || '',
          description: rebate.description || '',
          received_date: rebate.received_date ? rebate.received_date.split('T')[0] : '',
          receipt_url: rebate.receipt_url || ''
        });
      } else {
        setFormData({
          company_id: companyId || '',
          rebate_type: 'monthly',
          rebate_month: '',
          rebate_year: new Date().getFullYear(),
          amount: '',
          description: '',
          received_date: formatDateToISO(new Date()),
          receipt_url: ''
        });
      }
    }
  }, [isOpen, rebate, companyId]);

  // Fetch companies if not pre-selected
  useEffect(() => {
    if (isOpen && !companyId && !rebate) {
      fetchCompanies();
    }
  }, [isOpen, companyId, rebate]);

  // Fetch available periods when company is selected (only for new rebates)
  useEffect(() => {
    if (formData.company_id && !isEditing) {
      fetchAvailablePeriods(formData.company_id);
    } else if (!isEditing) {
      setAvailablePeriods(null);
    }
  }, [formData.company_id, isEditing]);

  // Reset month/year when type changes
  useEffect(() => {
    if (formData.rebate_type === 'yearly' && !isEditing) {
      setFormData((prev) => ({ ...prev, rebate_month: '' }));
    }
  }, [formData.rebate_type, isEditing]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await getCompanies();
      setCompanies(response.data || response || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePeriods = async (companyId) => {
    try {
      const response = await getAvailableRebatePeriods(companyId);
      setAvailablePeriods(response.data);
    } catch (error) {
      console.error('Error fetching available periods:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPEG, PNG, GIF, WebP) or PDF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);

      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('folder', 'rebate-receipts');

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setFormData((prev) => ({ ...prev, receipt_url: data.url || data.secure_url }));
      toast.success('Receipt uploaded');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload receipt');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveReceipt = () => {
    setFormData((prev) => ({ ...prev, receipt_url: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.company_id) {
      toast.error('Please select a company');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!isEditing && formData.rebate_type === 'monthly' && !formData.rebate_month) {
      toast.error('Please select a month');
      return;
    }
    if (!isEditing && formData.rebate_type === 'yearly' && !formData.rebate_year) {
      toast.error('Please select a year');
      return;
    }

    try {
      setSubmitting(true);

      if (isEditing) {
        // Update existing rebate
        await updateRebate(rebate.id, {
          amount: parseFloat(formData.amount),
          description: formData.description || null,
          received_date: formData.received_date || null,
          receipt_url: formData.receipt_url || null
        });
        toast.success('Rebate updated successfully');
      } else {
        // Create new rebate
        const payload = {
          company_id: parseInt(formData.company_id),
          rebate_type: formData.rebate_type,
          rebate_year:
            formData.rebate_type === 'monthly'
              ? availablePeriods?.monthly?.year
              : parseInt(formData.rebate_year),
          amount: parseFloat(formData.amount),
          description: formData.description || null,
          received_date: formData.received_date || null,
          receipt_url: formData.receipt_url || null
        };

        if (formData.rebate_type === 'monthly') {
          payload.rebate_month = parseInt(formData.rebate_month);
        }

        await createRebate(payload);
        toast.success('Rebate recorded successfully');
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving rebate:', error);
      toast.error(error.response?.data?.message || 'Failed to save rebate');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const availableMonths = availablePeriods?.monthly?.available_months || [];
  const availableYears = availablePeriods?.yearly?.available_years || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditing ? 'Edit Rebate' : 'Record Rebate'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Update rebate details' : 'Add company rebate/incentive'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Company Select (if not pre-selected and not editing) */}
          {!companyId && !isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                Company *
              </label>
              <select
                name="company_id"
                value={formData.company_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              >
                <option value="">Select company...</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Rebate Type Toggle (only for new rebates) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rebate Type *</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, rebate_type: 'monthly' }))}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    formData.rebate_type === 'monthly'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, rebate_type: 'yearly' }))}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    formData.rebate_type === 'yearly'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Yearly
                </button>
              </div>
            </div>
          )}

          {/* Period Selection (only for new rebates) */}
          {!isEditing && formData.company_id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {formData.rebate_type === 'monthly' ? 'Month *' : 'Year *'}
              </label>
              {formData.rebate_type === 'monthly' ? (
                <div>
                  <select
                    name="rebate_month"
                    value={formData.rebate_month}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select month...</option>
                    {availableMonths.length > 0 ? (
                      availableMonths.map((month) => (
                        <option key={month} value={month}>
                          {MONTH_NAMES[month - 1]} {availablePeriods?.monthly?.year}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        All months recorded for this year
                      </option>
                    )}
                  </select>
                  {availableMonths.length === 0 && formData.company_id && (
                    <p className="text-xs text-amber-600 mt-1">
                      All months for this year already have rebates recorded.
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <select
                    name="rebate_year"
                    value={formData.rebate_year}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select year...</option>
                    {availableYears.length > 0 ? (
                      availableYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        All years already recorded
                      </option>
                    )}
                  </select>
                  {availableYears.length === 0 && formData.company_id && (
                    <p className="text-xs text-amber-600 mt-1">
                      All years (last 3 years) already have rebates recorded.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Amount (₦) *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter rebate amount"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              required
            />
          </div>

          {/* Received Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Received</label>
            <input
              type="date"
              name="received_date"
              value={formData.received_date}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Upload className="w-4 h-4 inline mr-1" />
              Receipt (Optional)
            </label>
            {formData.receipt_url ? (
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-gray-500" />
                    <a
                      href={formData.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:underline truncate max-w-[200px]"
                    >
                      View Receipt
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveReceipt}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {formData.receipt_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                  <img
                    src={formData.receipt_url}
                    alt="Receipt"
                    className="mt-2 max-h-32 rounded border"
                  />
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-emerald-400 transition-colors ${uploading ? 'opacity-50' : ''}`}
                >
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload receipt</p>
                      <p className="text-xs text-gray-400 mt-1">Image or PDF (max 5MB)</p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional notes about this rebate..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || uploading}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                'Update Rebate'
              ) : (
                'Record Rebate'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RebateFormModal;
